import React from 'react';
import type { ColumnsType, ColumnType } from 'antd/es/table';

import {
  CheckMarkIcon,
  CopyIcon,
  SingleDotIcon,
} from '@shared/ui/icons';

import { twMerge } from '@utils/twMerge';
import { copyToClipboard } from '@utils/copyToClipboard';
import {
  HeaderAttrs,
  OnHeaderCell,
  QCTaskDetailSubheaderRow,
  QCTaskRow,
  QCTasksTableConfigOptions,
} from './types';
import {
  isDetail,
  isDetailSubheader,
  isDicomDetail,
  isDXADetail,
  isExecDetail,
  isExecSubheaderRow,
  isExecutionsSubheader,
  isModalitySubheader,
  lastColLabelForSubheader,
} from './qcHelpers';
import { defaultOpts } from './constants';
import {
  renderDateTime,
  renderNameEmail,
  renderStatus,
} from '../common/renderers';

const renderSubheaderCell = (
  text: string,
  headerTextClass?: string
): React.ReactNode => (
  <div
    className={twMerge('px-0 py-2 text-[14px] font-medium', headerTextClass)}
  >
    {text}
  </div>
);

const renderDicomUid = (uid?: string | null): React.ReactNode => {
  const trimmed = (uid ?? '').trim();

  if (!trimmed) {
    return <span>—</span>;
  }

  return (
    <div className='flex items-center min-w-0'>
      <span
        className='flex min-w-0 overflow-hidden text-ellipsis whitespace-nowrap'
        title={trimmed}
      >
        {trimmed}
      </span>

      <span
        className='shrink-0 ml-1 text-[16px] leading-none cursor-pointer'
        onClick={() => copyToClipboard(trimmed)}
      >
        <CopyIcon />
      </span>
    </div>
  );
};

const renderGroupActions = (iconClass: string) => (
  <div className='w-full flex items-center justify-center gap-1 pr-4'>
    <span className={iconClass}>{/* TBD in future */}</span>
    <span className={iconClass}>{/* TBD in future */}</span>
  </div>
);

const renderExecDetailActions = (iconClass: string) => (
  <div className='w-full flex items-center justify-center gap-1 pr-2'>
    <span className={iconClass}>{/* TBD in future */}</span>
    <span className={iconClass}>{/* TBD in future */}</span>
  </div>
);

const renderEmptyActions = () => (
  <div className='w-full flex items-center justify-center gap-1' />
);

export function buildColumns(
  opts?: QCTasksTableConfigOptions
): ColumnsType<QCTaskRow> {
  const mergedOpts = opts ? { ...defaultOpts, ...opts } : defaultOpts;

  const {
    iconColWidth,
    detailIconIndentPx,
    headerBgClass,
    rowBgClass,
    headerBgInline,
    rowBgInline,
    headerTextClass,
    dataFontSizePx,
    dataLineHeightPx,
    actionIconSizeClass,
    dateTextClass,
    timeTextClass,
  } = mergedOpts;

  const withHeader = (col: ColumnType<QCTaskRow>): ColumnType<QCTaskRow> => {
    const origOnHeaderCell = col.onHeaderCell;

    return {
      ...col,
      onHeaderCell: (...args: Parameters<OnHeaderCell>): HeaderAttrs => {
        const base: HeaderAttrs = origOnHeaderCell
          ? origOnHeaderCell(...args)
          : {};

        const style = base.style
          ? { ...base.style, background: headerBgInline }
          : { background: headerBgInline };

        return {
          ...base,
          className: twMerge(base.className ?? '', headerBgClass ?? ''),
          style,
        };
      },
    };
  };

  const dataTypographyInline: React.CSSProperties = {
    fontSize: `${dataFontSizePx}px`,
    lineHeight: `${dataLineHeightPx}px`,
  };

  const cellStyle = (
    row: QCTaskRow,
    extra?: React.CSSProperties
  ): React.CSSProperties => {
    const base = isDetailSubheader(row)
      ? { background: headerBgInline }
      : { background: rowBgInline, ...dataTypographyInline };

    return extra ? { ...base, ...extra } : base;
  };

  const cellClass = (row: QCTaskRow) =>
    isDetailSubheader(row) ? headerBgClass : rowBgClass || '';

  const columns: ColumnsType<QCTaskRow> = [
    {
      title: '',
      key: '__icon',
      width: iconColWidth,
      onHeaderCell: () => ({
        className: twMerge(headerBgClass ?? ''),
        style: { background: headerBgInline },
      }),
      render: (_: unknown, row: QCTaskRow) => {
        if (isDetailSubheader(row)) return null;

        const detail = isDetail(row);

        const isLabelDetailRow = [isDXADetail, isDicomDetail].some(fn =>
          fn(row)
        );

        const IconComponent = isLabelDetailRow ? SingleDotIcon : CheckMarkIcon;

        const iconClass = twMerge(
          'inline-flex items-center text-xl mt-1 [&>svg]:text-yellowGreen'
        );

        return (
          <span
            className={[
              'inline-flex items-center w-full pl-2',
              detail ? 'justify-start' : 'justify-center',
            ].join(' ')}
          >
            <span className={iconClass}>
              <IconComponent />
            </span>
          </span>
        );
      },
      onCell: (row: QCTaskRow) => {
        const isSubheader = isDetailSubheader(row);
        const isDetailRow = isDetail(row) && !isSubheader;

        let style: React.CSSProperties;
        if (isDetailRow) {
          style = {
            background: rowBgInline,
            fontSize: `${dataFontSizePx}px`,
            lineHeight: `${dataLineHeightPx}px`,
            paddingLeft: detailIconIndentPx,
          };
        } else if (isSubheader) {
          style = { background: headerBgInline };
        } else {
          style = {
            background: rowBgInline,
            fontSize: `${dataFontSizePx}px`,
            lineHeight: `${dataLineHeightPx}px`,
          };
        }

        return {
          className: isSubheader ? headerBgClass : rowBgClass || '',
          style,
        };
      },
    },

    withHeader({
      title: 'Task',
      key: 'col1',
      width: 200,
      render: (_: unknown, row: QCTaskRow) => {
        if (row.type === 'group') {
          return <span className='font-medium'>{row.task}</span>;
        }

        if (isExecutionsSubheader(row)) {
          return renderSubheaderCell('Executions', headerTextClass);
        }

        if (isModalitySubheader(row)) {
          return renderSubheaderCell('Modality', headerTextClass);
        }

        if (isExecDetail(row)) {
          return row.label ?? '—';
        }

        if ([isDXADetail, isDicomDetail].some(fn => fn(row))) {
          return row.label ?? '—';
        }

        return '—';
      },
      onCell: (row: QCTaskRow) => {
        const baseStyle = cellStyle(row, {
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        });

        if (row.type === 'group') {
          return {
            className: cellClass(row),
            style: baseStyle,
          };
        }

        if (isDetail(row) && !isDetailSubheader(row)) {
          return {
            className: cellClass(row),
            style: {
              ...baseStyle,
              paddingLeft: 15,
            },
          };
        }

        return {
          className: cellClass(row),
          style: baseStyle,
        };
      },
    }),

    withHeader({
      title: 'Started',
      key: 'col2',
      width: 180,
      render: (_: unknown, row: QCTaskRow) => {
        if (row.type === 'group') {
          return renderDateTime(row.started, dateTextClass, timeTextClass);
        }

        if (isExecSubheaderRow(row)) {
          return renderSubheaderCell('', headerTextClass);
        }

        if (isModalitySubheader(row)) {
          return renderSubheaderCell('Label', headerTextClass);
        }

        if (isExecDetail(row)) {
          return renderDateTime(row.startDate, dateTextClass, timeTextClass);
        }

        if ([isDXADetail, isDicomDetail].some(fn => fn(row))) {
          if (
            'detailLabel' in row &&
            typeof row.detailLabel === 'string' &&
            row.detailLabel
          ) {
            return row.detailLabel;
          }
          return row.label ?? '—';
        }

        return null;
      },
      onCell: (row: QCTaskRow) => ({
        className: cellClass(row),
        style: cellStyle(row),
      }),
    }),

    withHeader({
      title: 'Finished',
      key: 'col3',
      width: 180,
      render: (_: unknown, row: QCTaskRow) => {
        if (row.type === 'group') {
          return renderDateTime(row.finished, dateTextClass, timeTextClass);
        }

        if (isExecSubheaderRow(row)) {
          return renderSubheaderCell('', headerTextClass);
        }

        if (isModalitySubheader(row)) {
          return renderSubheaderCell('Acquisition Date', headerTextClass);
        }

        if (isExecDetail(row)) {
          return renderDateTime(row.endDate, dateTextClass, timeTextClass);
        }

        if ([isDXADetail, isDicomDetail].some(check => check(row))) {
          return renderDateTime(row.acquisitionDate, dateTextClass);
        }

        return null;
      },
      onCell: (row: QCTaskRow) => ({
        className: cellClass(row),
        style: cellStyle(row),
      }),
    }),

    withHeader({
      title: 'Assignee',
      key: 'col4',
      width: 200,
      render: (_: unknown, row: QCTaskRow) => {
        if (row.type === 'group') {
          return renderNameEmail(
            row.assignee,
            row.assigneeEmail,
            dateTextClass,
            timeTextClass
          );
        }

        if (isExecSubheaderRow(row)) {
          return null;
        }

        if (isDetailSubheader(row)) {
          const sub = row as QCTaskDetailSubheaderRow;
          return renderSubheaderCell(
            lastColLabelForSubheader(sub.detailKind),
            headerTextClass
          );
        }

        if (isExecDetail(row)) {
          return renderNameEmail(
            row.assignee,
            row.assigneeEmail,
            dateTextClass,
            timeTextClass
          );
        }

        if (isDXADetail(row)) {
          return row.workstation ?? '—';
        }

        if (isDicomDetail(row)) {
          const uid = row.dicomSeriesUid;
          return renderDicomUid(uid);
        }

        return null;
      },
      onCell: (row: QCTaskRow) => {
        const base = {
          className: isDetailSubheader(row) ? headerBgClass : rowBgClass || '',
          style: isDetailSubheader(row)
            ? { background: headerBgInline }
            : {
                background: rowBgInline,
                fontSize: `${dataFontSizePx}px`,
                lineHeight: `${dataLineHeightPx}px`,
              },
        };

        if (isDicomDetail(row)) {
          return {
            ...base,
            colSpan: 2,
            style: { ...base.style, paddingRight: 0 },
          };
        }

        return base;
      },
    }),

    withHeader({
      title: 'Status',
      key: 'col5',
      width: 160,
      render: (_: unknown, row: QCTaskRow) => {
        if (row.type === 'group') {
          return renderStatus(row.status);
        }

        if (isExecutionsSubheader(row)) {
          return null;
        }

        if (isExecDetail(row)) {
          return renderStatus(row.status);
        }

        if (isDicomDetail(row)) {
          return null;
        }

        return null;
      },
      onCell: (row: QCTaskRow) => {
        const base = {
          className: cellClass(row),
          style: cellStyle(row),
        };

        if (isDicomDetail(row)) {
          return {
            ...base,
            colSpan: 0,
          };
        }

        return base;
      },
    }),

    withHeader({
      title: 'Actions',
      key: 'actions',
      width: 90,
      align: 'center' as const,
      render: (_: unknown, row: QCTaskRow) => {
        if (row.type === 'group') {
          const iconClass = twMerge('shrink-0', actionIconSizeClass);
          return renderGroupActions(iconClass);
        }

        if (isDetailSubheader(row)) {
          return null;
        }

        if (isExecDetail(row)) {
          const iconClass = twMerge('shrink-0', actionIconSizeClass);
          return renderExecDetailActions(iconClass);
        }

        return renderEmptyActions();
      },
      onCell: (row: QCTaskRow) => ({
        className: cellClass(row),
        style: cellStyle(row, { paddingLeft: 0, paddingRight: 0 }),
      }),
    }),
  ];

  return columns;
}
